import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  api_v3_get_division_by_id,
  api_v3_get_classification,
  api_v3_get_tournament_by_id,
  api_v3_get_matches_by_tournament_division_group,
} from "../utils/constants";
import { ListView, Loading } from "../components";

function fmtYMD(d) {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

const DivisionPage = () => {
  const { divisionId, groupId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [division, setDivision] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [tournamentId, setTournamentId] = useState(null);
  const [tournamentName, setTournamentName] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [showDivisions, setShowDivisions] = useState(false);
  const [classification, setClassification] = useState(null);

  const [tickerMatches, setTickerMatches] = useState([]);

  useEffect(() => {
    if (!divisionId || !groupId) return;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const divRes = await axios.get(api_v3_get_division_by_id(divisionId));
        if (divRes.status !== 200) { setError(true); setLoading(false); return; }
        const divInfo = divRes.data;
        setDivision(divInfo);
        setTournamentId(divInfo.tournamentId);

        const group = (divInfo.groups || []).find((g) => g.groupId === groupId);
        if (group) setGroupInfo(group);

        const classRes = await axios.get(api_v3_get_classification(divisionId, groupId));
        if (classRes.status === 200) setClassification(classRes.data);

        const tournRes = await axios.get(api_v3_get_tournament_by_id(divInfo.tournamentId));
        if (tournRes.status === 200) {
          const tData = tournRes.data;
          if (tData.name) setTournamentName(tData.name);
          const sorted = (tData.divisions || []).sort((a, b) => a.sequence > b.sequence ? 1 : -1);
          setDivisions(sorted);
        }

        setLoading(false);
      } catch (err) {
        setError(true);
        setLoading(false);
      }
    };

    load();
  }, [divisionId, groupId]);

  useEffect(() => {
    if (!tournamentId || !divisionId || !groupId) return;
    const loadMatches = async () => {
      try {
        const res = await axios.get(
          api_v3_get_matches_by_tournament_division_group(tournamentId, divisionId, groupId)
        );
        if (res.status === 200) setTickerMatches(res.data || []);
      } catch (_) {}
    };
    loadMatches();
  }, [tournamentId, divisionId, groupId]);

  const today = new Date();
  const todayStr = fmtYMD(today);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = fmtYMD(tomorrow);

  const todayMatches = tickerMatches
    .filter((m) => {
      if (m.status === "HIDDEN") return false;
      if (!m.matchDate) return false;
      return fmtYMD(new Date(m.matchDate)) === todayStr;
    })
    .sort((a, b) => new Date(a.matchDate) - new Date(b.matchDate));
  const tomorrowMatches = tickerMatches
    .filter((m) => {
      if (m.status === "HIDDEN") return false;
      if (!m.matchDate) return false;
      return fmtYMD(new Date(m.matchDate)) === tomorrowStr;
    })
    .sort((a, b) => new Date(a.matchDate) - new Date(b.matchDate));

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("es-ES", {
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid",
      });
    } catch { return ""; }
  };

  if (loading) {
    return (
      <Canvas>
        <Loading />
      </Canvas>
    );
  }

  if (error || !division) {
    return (
      <Canvas>
        <ErrorBox>Error cargando los datos de la clasificación.</ErrorBox>
      </Canvas>
    );
  }

  return (
    <Canvas className="v-noise">
      <Inner>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BcrumbLink to={`/`}>{tournamentName || "Inicio"}</BcrumbLink>
          <Sep>/</Sep>
          <BcrumbCurrent onClick={() => setShowDivisions(!showDivisions)}>
            {division.name}
          </BcrumbCurrent>
          <Sep>/</Sep>
          <BcrumbDim>{groupInfo?.name}</BcrumbDim>
        </Breadcrumb>

        {showDivisions && divisions.length > 0 && (
          <DivDropdown>
            {divisions.map((d) => (
              <DivOption key={d.divisionId} onClick={() => setShowDivisions(false)}>
                <Link to={`/division/${d.divisionId}/group/${(d.groups && d.groups[0])?.groupId || ""}`}>
                  {d.name}
                </Link>
              </DivOption>
            ))}
          </DivDropdown>
        )}

        {/* Group pills + phase */}
        <TopBar>
          <GroupPills>
            {division.groups?.map((group) => (
              <Pill
                key={group.groupId}
                as={Link}
                to={`/division/${division.divisionId}/group/${group.groupId}`}
                className={group.groupId === groupId ? "active" : ""}
              >
                {group.name}
              </Pill>
            ))}
          </GroupPills>
          <PhasePill className="active">LIGA</PhasePill>
        </TopBar>

        {/* Classification */}
        {classification ? (
          <ListView classification={classification} />
        ) : (
          <Loading />
        )}

        {/* Próximos Partidos */}
        <UpcomingSection>
          <UpcomingTitle>Próximos Partidos</UpcomingTitle>
          {todayMatches.length === 0 && tomorrowMatches.length === 0 ? (
            <EmptyText>No hay partidos los próximos días</EmptyText>
          ) : (
            <>
              {todayMatches.length > 0 && (
                <>
                  <DayLabel>HOY</DayLabel>
                  <UpcomingList>
                    {todayMatches.map((m) => (
                      <UpcomingCard key={m.matchId} to={`/match/${m.matchId}/detail`}>
                        <UpcomingTime>{formatTime(m.matchDate)}</UpcomingTime>
                        <UpcomingTeams>
                          <UpcomingPlayer>{m.playerAusername}</UpcomingPlayer>
                          <UpcomingVs>VS</UpcomingVs>
                          <UpcomingPlayer>{m.playerBusername}</UpcomingPlayer>
                        </UpcomingTeams>
                      </UpcomingCard>
                    ))}
                  </UpcomingList>
                </>
              )}
              {tomorrowMatches.length > 0 && (
                <>
                  {todayMatches.length > 0 && <Spacer />}
                  <DayLabel>MAÑANA</DayLabel>
                  <UpcomingList>
                    {tomorrowMatches.map((m) => (
                      <UpcomingCard key={m.matchId} to={`/match/${m.matchId}/detail`}>
                        <UpcomingTime>{formatTime(m.matchDate)}</UpcomingTime>
                        <UpcomingTeams>
                          <UpcomingPlayer>{m.playerAusername}</UpcomingPlayer>
                          <UpcomingVs>VS</UpcomingVs>
                          <UpcomingPlayer>{m.playerBusername}</UpcomingPlayer>
                        </UpcomingTeams>
                      </UpcomingCard>
                    ))}
                  </UpcomingList>
                </>
              )}
            </>
          )}
        </UpcomingSection>
      </Inner>
    </Canvas>
  );
};

const Canvas = styled.main`
  background: var(--v-canvas);
  min-height: 100vh;
  padding: 1.5rem;
  position: relative;

  @media (min-width: 1024px) {
    padding: 2.5rem;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const ErrorBox = styled.div`
  font-family: var(--v-font-body);
  font-size: 16px;
  color: var(--v-secondary);
  text-align: center;
  padding: 4rem 2rem;
`;

/* Breadcrumb */
const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Sep = styled.span`
  font-family: var(--v-font-mono);
  font-size: 12px;
  color: var(--v-secondary);
  opacity: 0.3;
`;

const BcrumbLink = styled(Link)`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-secondary);
  text-decoration: none;
  transition: color var(--v-transition);

  &:hover {
    color: var(--v-hover);
  }
`;

const BcrumbCurrent = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-mint);
  cursor: pointer;
  transition: opacity var(--v-transition);

  &:hover {
    opacity: 0.7;
  }
`;

const BcrumbDim = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-secondary);
  opacity: 0.6;
`;

/* Divisions dropdown */
const DivDropdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: var(--v-surface);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
`;

const DivOption = styled.div`
  a {
    font-family: var(--v-font-mono);
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--v-secondary);
    text-decoration: none;
    padding: 3px 10px;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 999px;
    transition: border-color var(--v-transition), color var(--v-transition);

    &:hover {
      border-color: rgba(255,255,255,0.15);
      color: var(--v-white);
    }
  }
`;

/* Próximos Partidos */
const UpcomingSection = styled.div`
  margin-top: 1.5rem;
`;

const UpcomingTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
  margin: 0 0 0.75rem 0;
`;

const UpcomingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const UpcomingCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: border-color var(--v-transition), background var(--v-transition);

  &:hover {
    border-color: rgba(60, 255, 208, 0.2);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const UpcomingTime = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--v-secondary);
  white-space: nowrap;
  min-width: 3.5rem;
`;

const UpcomingTeams = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const UpcomingPlayer = styled.span`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 500;
  color: var(--v-white);
`;

const UpcomingVs = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--v-secondary);
  opacity: 0.3;
`;

const DayLabel = styled.div`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-secondary);
  margin-bottom: 0.5rem;
`;

const Spacer = styled.div`
  height: 0.75rem;
`;

const EmptyText = styled.div`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-secondary);
  opacity: 0.6;
`;

/* Top bar: group pills + phase */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const GroupPills = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const Pill = styled(Link)`
  display: inline-block;
  padding: 6px 16px;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-secondary);
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  text-decoration: none;
  transition: border-color var(--v-transition), color var(--v-transition), background var(--v-transition);

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    color: var(--v-white);
    background: rgba(255, 255, 255, 0.04);
  }

  &.active {
    color: var(--v-black);
    background: var(--v-mint);
    border-color: var(--v-mint);
  }
`;

const PhasePill = styled.span`
  display: inline-block;
  padding: 6px 16px;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 999px;
  margin-left: auto;

  &.active {
    color: var(--v-black);
    background: var(--v-gold);
    border-color: var(--v-gold);
  }
`;

export default DivisionPage;
